import { CallRoute } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { DeviceInclude_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import { CallGroupApi } from '@frontend/api-call-group';
import CallQueueAPI from '@frontend/api-call-queue';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DepartmentsApi } from '@frontend/api-departments';
import { DevicesQueries } from '@frontend/api-devices';
import { ForwardingNumberApi } from '@frontend/api-forwarding-number';
import PhoneHoursApi from '@frontend/api-phone-hours';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import SipProfileAPI from '@frontend/api-sip-profile';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useScopedQuery } from '@frontend/scope';
import { queryKeys } from '../../query-keys';
import { useAudioQuery } from '../../utils/use-audio-query';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';
import useVoicemailBoxes from '../voicemail-box/use-voicemail-boxes';

export const useCallObjectsData = (
  settingsTenantLocation?: SettingsTenantLocation,
  queryOptions?: { staleTime?: number; cacheTime?: number }
) => {
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const tenantLocationIds = useTenantLocationIds();

  const [isCreditEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  // Fetch call groups
  const { data: callGroups = [], isLoading: callGroupsIsLoading } = useScopedQuery({
    queryKey: queryKeys.listCallGroups(tenantId),
    queryFn: () => CallGroupApi.listCallGroups({ tenantId: tenantId }),
    enabled: !!tenantId,
    select: (data) =>
      (data.callGroups ?? []).sort((a, b) => a.name.localeCompare(b.name)).filter(({ name }) => !name.includes('e911')),
    staleTime: queryOptions?.staleTime ?? 0,
    retry: false,
    meta: {
      error: {
        message: 'useCallObjectsData:callGroupsQuery | Failed to fetch call groups',
        data: { tenantId },
      },
    },
  });

  // Fetch devices with addresses
  const { data: sipProfiles = [], isLoading: sipProfilesIslLoading } = useQuery({
    queryKey: queryKeys.devicesWithAddresses(),
    queryFn: () => SipProfileAPI.List({ tenantId: tenantId }).then((res) => res.sipProfiles),
    select: (data) =>
      (data ?? []).sort((a, b) => {
        const aName = a.device?.deviceName || '';
        const bName = b.device?.deviceName || '';
        return aName.localeCompare(bName);
      }),
    retry: false,
    staleTime: queryOptions?.staleTime ?? 0,
    enabled: !!tenantId,
    meta: {
      error: {
        message: 'useCallObjectsData:sipProfilesQuery | Failed to fetch devices with addresses',
        data: { tenantId },
      },
    },
  });

  // Fetch call forwarding numbers
  const { data: callForwardingNumbers = [], isLoading: callForwardingNumbersIsLoading } = useQuery({
    queryKey: queryKeys.forwardingNumbers(),
    queryFn: () => ForwardingNumberApi.list({ locationId: settingsTenantLocation?.id }),
    select: (data) =>
      (data ?? []).sort((a, b) => {
        const aName = a.name || '';
        const bName = b.name || '';
        return aName.localeCompare(bName);
      }),
    retry: false,
    enabled: !!settingsTenantLocation?.id,
    meta: {
      error: {
        message: 'useCallObjectsData:callForwardingNumbersQuery | Failed to fetch forwarding numbers',
        data: { locationId: settingsTenantLocation?.id },
      },
    },
  });

  // Fetch call queues
  const { data: callQueues = [], isLoading: callQueuesIsLoading } = useQuery({
    queryKey: [tenantId, ...queryKeys.settings.listCallQueue()],
    queryFn: () => CallQueueAPI.List({ tenantId: tenantId ?? '' }).then((res) => res.callQueues),
    select: (data) => {
      return (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!tenantId,
    staleTime: queryOptions?.staleTime ?? 0,
    retry: false,
    meta: {
      error: {
        message: 'useCallObjectsData:callQueuesQuery | Failed to fetch call queues',
        data: { tenantId },
      },
    },
  });

  // Fetch all phone numbers for tenant locations
  const { data: allPhoneNumbers, isLoading: allPhoneNumbersIsLoading } =
    PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
      request: { locationIds: tenantLocationIds, voice: true, excludeTenDlc: true },
      options: { enabled: !!tenantLocationIds.length, select: (data) => data?.phoneNumbers ?? [], retry: false },
    });

  // Fetch devices
  const { data: devices = [], isLoading: devicesIsLoading } = DevicesQueries.useGetDevicesList(
    {
      include: [DeviceInclude_Enum.CALL_GROUPS, DeviceInclude_Enum.E911_ADDRESS, DeviceInclude_Enum.REGISTRATION],
      locationIds: tenantLocationIds,
      tenantId: settingsTenantLocation?.phoneTenantId,
    },
    {
      select: (data) => (data?.devices ?? []).sort((a, b) => a.name.localeCompare(b.name)),
      enabled: !!settingsTenantLocation?.phoneTenantId,
      retry: false,
      staleTime: queryOptions?.staleTime ?? 0,
    }
  );

  // Fetch forwarding numbers
  const { data: forwardingNumbers = [], isLoading: forwardingNumbersIsLoading } =
    PhoneNumbersV1.Queries.useListForwardingNumbersQuery({
      request: { tenantId },
      options: {
        enabled: !!tenantId,
        select: (data) =>
          (data?.forwardingNumbers ?? []).sort((a, b) => {
            const nameA = a.name?.toLowerCase() ?? '';
            const nameB = b.name?.toLowerCase() ?? '';

            return nameA.localeCompare(nameB);
          }) ?? [],
        retry: false,
        staleTime: queryOptions?.staleTime ?? 0,
      },
    });

  // Fetch voicemail boxes
  const { data: voicemailBoxes, isLoading: voicemailBoxesIsLoading } = useVoicemailBoxes({
    phoneTenantId: settingsTenantLocation?.phoneTenantId ?? '',
    options: {
      retry: false,
      staleTime: queryOptions?.staleTime ?? 0,
    },
  });

  // Fetch departments
  const { data: departments = [], isLoading: departmentsIsLoading } = useQuery({
    queryKey: [queryKeys.listDepartments()],
    queryFn: () => {
      return DepartmentsApi.listDept({});
    },
    select: (data) => data.departments ?? [],
    staleTime: queryOptions?.staleTime ?? 0,
    meta: {
      error: {
        message: 'useCallObjectsData:departmentsQuery | Failed to fetch departments',
        data: {},
      },
    },
  });

  // Fetch call routes
  const { data: routes = [], isLoading: routesIsLoading } = CallRouteV1.Queries.useListQuery({
    request: {
      tenantId: settingsTenantLocation?.phoneTenantId ?? '',
      includes: { deviceExtensionRoutes: true },
    },
    options: {
      enabled: !!settingsTenantLocation?.phoneTenantId,
      select: ({ callRoutes, deviceExtensionRoutes }) => {
        // Sort the extension numbers so that they are displayed in ascending order.
        const sortedCallRoutes = (callRoutes ?? []).reduce<CallRoute[]>((acc, callroute) => {
          return [
            ...acc,
            {
              ...callroute,
              extensionNumbers: callroute.extensionNumbers.sort(),
            },
          ];
        }, []);

        const sortedDeviceRoutes = (deviceExtensionRoutes ?? []).reduce<CallRoute[]>((acc, deviceRoute) => {
          return [
            ...acc,
            {
              ...deviceRoute,
              extensionNumbers: deviceRoute.extensionNumbers.sort(),
            },
          ];
        }, []);

        return [...sortedCallRoutes, ...sortedDeviceRoutes];
      },
      cacheTime: queryOptions?.cacheTime ?? 1000,
      staleTime: queryOptions?.staleTime ?? 1000,
    },
  });

  // Fetch phone trees
  const { data: phoneTrees = [], isLoading: phoneTreesIsLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listPhoneTrees()],
    queryFn: () =>
      PhoneTreeAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then((res) => res.phoneTrees),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    staleTime: queryOptions?.staleTime ?? 1 * 60 * 1000,
    meta: {
      error: {
        message: 'useCallObjectsData:phoneTreesQuery | Failed to fetch phone trees',
        data: { phoneTenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  // Fetch phone hours
  const { data: phoneHours = [], isLoading: phoneHoursIsLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listPhoneHours()],
    queryFn: async () =>
      PhoneHoursApi.ListPhoneHours({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then(
        (res) => res.phoneHours
      ),
    select: (data) => (data ?? [])?.sort((a, b) => a.name.localeCompare(b.name)),
    staleTime: queryOptions?.staleTime ?? 1 * 60 * 1000,
    meta: {
      error: {
        message: 'useCallObjectsData:phoneHoursQuery | Failed to fetch phone hours',
        data: { phoneTenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  // Fetch audio files
  const { data: audioFiles, isLoading: audioFilesIsLoading } = useAudioQuery({
    tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    allowedOptions: { custom: true, standard: true, mailbox: false },
  });

  const { data: locationCreditDetails = {}, isLoading: locationCreditDetailsIsLoading } =
    PlatformLocationFeatureV1.Queries.useGetLocationFeatureCreditDetailsQuery({
      request: {
        locationIds: tenantLocationIds,
        featureEnum: Feature.TRUELARK_AI_VOICE,
      },
      options: {
        enabled: isCreditEnforcementEnabled && tenantLocationIds.length > 0,
        cacheTime: 0,
        staleTime: 0,
        select: (data) => data?.locationCreditDetails ?? {},
      },
    });

  return {
    callGroups,
    callGroupsIsLoading,
    callForwardingNumbers,
    callForwardingNumbersIsLoading,
    callQueues,
    callQueuesIsLoading,
    routes,
    routesIsLoading,
    devices,
    devicesIsLoading,
    forwardingNumbers,
    forwardingNumbersIsLoading,
    sipProfiles,
    sipProfilesIslLoading,
    voicemailBoxes,
    voicemailBoxesIsLoading,
    allPhoneNumbers,
    allPhoneNumbersIsLoading,
    departments,
    departmentsIsLoading,
    phoneTrees,
    phoneTreesIsLoading,
    phoneHours,
    phoneHoursIsLoading,
    audioFiles,
    audioFilesIsLoading,
    locationCreditDetails,
    locationCreditDetailsIsLoading,
    someIsLoading:
      callGroupsIsLoading ||
      callForwardingNumbersIsLoading ||
      callQueuesIsLoading ||
      routesIsLoading ||
      devicesIsLoading ||
      forwardingNumbersIsLoading ||
      sipProfilesIslLoading ||
      voicemailBoxesIsLoading ||
      allPhoneNumbersIsLoading ||
      departmentsIsLoading ||
      phoneTreesIsLoading ||
      phoneHoursIsLoading ||
      audioFilesIsLoading ||
      locationCreditDetailsIsLoading,
  };
};
